<!-- Creator edit dialog (#creatorEditModal, the "profile card" of a creator, data in data.creators): new or existing
     creator. Fields in order: banner (the wide image behind the photo in the detail, same box menu as the photo with
     "Remove banner"; 1600 px, stored apart), photo (a click opens the box menu: upload from the computer, from a link, remove; or drop an
     image file or a web image; shrunk to 400 px and stored apart; ImageBox.vue without URL field and trash button), project (with at least two projects; move, or copy to another
     project, for a saved creator), name (required), type (Collaborator / Competitor, one or both), YouTube, Instagram,
     TikTok, Twitch ("https://" added on save), bio with @mentions, strengths, weaknesses, rating, notes with @mentions.
     A new creator starts with the given stored type (by default the list open on the Creators page, otherwise
     Competitor) and the project of new items. Delete asks first. Opened from the creator detail ("Edit Profile" or the
     creator card), closing it (×, ‹ or Save) opens the detail again; after a delete it does not.
     Opened with openCreatorEditModal(id?, type?) (create button, Creators page "Add") through the creators
     store; logic in src/features/creators.ts. Like the legacy dialog, it closes only with ‹, × or the buttons; Enter in the name, Ctrl/Cmd+S and Ctrl/Cmd+Enter
     save. From 640 px the socials and strengths/weaknesses go two by two; from 1024 px the photo is beside project, name
     and type. -->
<script setup lang="ts">
import EditDialogFooter from '../fields/EditDialogFooter.vue'
import { INPUT_CLS, requiredCls } from '../../utils/fieldCls'
import { nextTick, onMounted, reactive, ref, watch } from 'vue'
import AppDialog from '../AppDialog.vue'
import ImageBox from '../fields/ImageBox.vue'
import ProjectPicker from '../fields/ProjectPicker.vue'
import MentionField from '../fields/MentionField.vue'
import PillGroup from '../fields/PillGroup.vue'
import { useI18n } from '../../composables/useI18n'
import { useDataStore } from '../../stores/data'
import { useCreatorsStore } from '../../stores/creators'
import { creatorForm, deleteCreator, saveCreator } from '../../features/creators'
import { validProject } from '../../features/projects'
import { newItemProject } from '../../stores/projects'
import { confirmDelete, uiAlertThen } from '../../stores/notices'
import { enterKey } from '../../features/dialogs'
import { finePointer } from '../../utils/media'
import { saveAppData } from '../../features/storage/save'
import { SOCIAL_FIELDS } from '../../data/profiles'

const props = defineProps<{ id: string | null; type: string; fromDetail: boolean }>()
const { t } = useI18n()
const store = useDataStore()
const creators = useCreatorsStore()

const form = reactive(creatorForm(store.getData(), props.id, props.type, newItemProject()))

const TYPES = [{ value: 'Collaboratore', label: t('Collaborator') }, { value: 'Competitor', label: 'Competitor' }]
const VERDICTS = [{ value: '', label: t('To evaluate') }, { value: 'Promosso', label: t('Approved') }, { value: 'Bocciato', label: t('Rejected') }]

// A new creator starts in the name (with a mouse: no phone keyboard over the dialog); the name is required: shown in the
// main colour, with the cursor in it, after the message
const nameInput = ref<HTMLInputElement | null>(null)
onMounted(() => { if (!props.id && finePointer()) nextTick(() => nameInput.value?.focus()) })
const missing = ref(false)
watch(() => form.name, v => { if (v.trim()) missing.value = false })
const onEnter = enterKey(save)

function save() {
  if (!form.name.trim()) { missing.value = true; void uiAlertThen(t("Enter the creator's name!")).then(() => nameInput.value?.focus()); return }
  const d = store.getData()
  if (!saveCreator(d, props.id, { ...form, projectId: validProject(d, form.projectId, newItemProject()) })) return
  saveAppData(d)
  creators.closeEdit()
}

async function remove() {
  if (!props.id) return
  if (!(await confirmDelete(t('Are you sure you want to delete this creator?')))) return
  const d = store.getData()
  deleteCreator(d, props.id)
  saveAppData(d)
  creators.closeEdit(false)
}
</script>

<template>
  <AppDialog id="creatorEditModal" name="creatorEdit" title-id="creatorEditModalTitle" :title="id ? t('Edit creator') : t('New creator')" width="max-w-2xl"
    body-class="p-6 overflow-y-auto space-y-6 flex-1 text-sm"
    editing :save="save" @close="creators.closeEdit()">
    <!-- Banner: the wide image behind the photo in the creator's profile (same box menu as the photo; 1600 px, stored apart) -->
    <div>
      <ImageBox v-model="form.banner" id="creatorBannerBox" file-id="creatorBannerFileInput" :max="1600" :remove="false" menu-remove :remove-label="t('Remove banner')" :placeholder="t('Banner')"
        box-class="w-full h-24 sm:h-32 rounded-xl bg-thumbBg border border-red-950/50 flex items-center justify-center cursor-pointer relative overflow-hidden bg-cover bg-center transition hover:border-brand">
        <div id="creatorBannerPlaceholder" class="flex flex-col items-center text-center text-brand space-y-1 px-4">
          <span class="text-xs font-bold"><i class="fas fa-image mr-1.5"></i>{{ t('Banner') }}</span>
          <span class="text-[11px] text-gray-500">{{ t('A wide image shown behind the photo, like a channel banner') }}</span>
        </div>
      </ImageBox>
    </div>

    <div class="space-y-6 lg:space-y-0 lg:flex lg:gap-6 lg:items-start">
    <div class="flex flex-col items-center lg:flex-shrink-0">
      <ImageBox v-model="form.avatar" id="creatorAvatarBox" file-id="creatorAvatarFileInput" :max="400" :remove="false" menu-remove
        box-class="w-24 h-24 rounded-full bg-thumbBg border border-red-950/50 flex flex-col items-center justify-center cursor-pointer relative overflow-hidden bg-cover bg-center transition hover:border-brand">
        <div id="creatorAvatarPlaceholder" class="flex flex-col items-center text-brand space-y-1">
          <i class="fas fa-camera text-xl"></i>
          <span class="text-[10px] font-bold">Avatar</span>
        </div>
      </ImageBox>
    </div>

    <div class="space-y-6 lg:flex-1 min-w-0">
    <ProjectPicker v-model="form.projectId" kind="creator" :item-id="id" />
    <div>
      <label class="block text-xs font-bold text-gray-400 mb-2 tracking-wider">{{ t('NAME') }}</label>
      <input id="inputCreatorName" ref="nameInput" v-model="form.name" type="text" :placeholder="t('E.g. Slends')" :class="requiredCls(missing)" @keydown.enter="onEnter">
    </div>

    <div>
      <label class="block text-xs font-bold text-gray-400 mb-2 tracking-wider"><span>{{ t('TYPE') }}</span> <span class="font-normal text-gray-500 normal-case tracking-normal">{{ t('(one or both)') }}</span></label>
      <PillGroup id="groupCreatorType" v-model="form.types" :options="TYPES" multiple keep-one class="flex gap-2" />
    </div>
    </div>
    </div>

    <div class="grid sm:grid-cols-2 gap-6 sm:gap-4">
    <div v-for="[k, label, ph] in SOCIAL_FIELDS" :key="k">
      <label class="block text-xs font-bold text-gray-400 mb-2 tracking-wider">{{ label }}</label>
      <input :id="'inputCreator' + k[0].toUpperCase() + k.slice(1)" v-model="form[k]" type="url" inputmode="url" autocapitalize="off" autocorrect="off" spellcheck="false" autocomplete="off" :placeholder="ph" :class="INPUT_CLS">
    </div>
    </div>

    <div><label class="block text-xs font-bold text-gray-400 mb-2 tracking-wider">{{ t('CREATOR BIO') }}</label><MentionField id="inputCreatorBio" v-model="form.bio" :placeholder="t('Who they are, what they talk about, what their channel is like... (@ to mention)')" class="min-h-[5rem] w-full bg-darkinput border border-gray-800 rounded-lg p-3 text-gray-100 focus:outline-none focus:border-brand transition" /></div>
    <div class="grid sm:grid-cols-2 gap-6 sm:gap-4">
    <div><label class="block text-xs font-bold text-gray-400 mb-2 tracking-wider">{{ t('STRENGTHS') }}</label><MentionField id="inputCreatorStrengths" v-model="form.strengths" class="min-h-[4.5rem] w-full bg-darkinput border border-gray-800 rounded-lg p-3 text-gray-100 transition" /></div>
    <div><label class="block text-xs font-bold text-gray-400 mb-2 tracking-wider">{{ t('WEAKNESSES') }}</label><MentionField id="inputCreatorWeaknesses" v-model="form.weaknesses" class="min-h-[4.5rem] w-full bg-darkinput border border-gray-800 rounded-lg p-3 text-gray-100 transition" /></div>
    </div>
    <div><label class="block text-xs font-bold text-gray-400 mb-2 tracking-wider">{{ t('RATING') }}</label><PillGroup id="groupCreatorVerdict" v-model="form.verdict" :options="VERDICTS" class="flex gap-2 flex-wrap" /></div>

    <div>
      <label class="block text-xs font-bold text-gray-400 mb-2 tracking-wider">{{ t('YOUR NOTES') }}</label>
      <MentionField id="inputCreatorNotes" v-model="form.notes" :placeholder="t('Style, niche, remarks... (@ to mention)')"
        class="min-h-[5rem] w-full bg-darkinput border border-gray-800 rounded-lg p-4 text-gray-100 focus:outline-none focus:border-brand transition" />
    </div>

    <template #footer>
      <EditDialogFooter delete-id="deleteCreatorBtn" :can-delete="!!id" :delete-label="t('Delete creator')" @delete="remove" @save="save" />
    </template>
  </AppDialog>
</template>
